import React, { useCallback, useEffect, useState } from "react";
import classnames from "classnames";
import axios from "axios";
import { Upload } from "antd";
import { IUploader, IUploadFile } from "./interface";
import IconButton from "../IButton/IconButton";
import UploadItemPreview from "./UploadItemPreview";
import fileApi from "../../api/Files";
import styles from "./UploadFile.module.scss";
import { observer } from "mobx-react-lite";
import { StatusInfo } from "../../store/core";
import { useStatusMessage } from "../../hooks/useStatusMessage";
import Constants from "../../constants/Common";
const statusFile = new StatusInfo();

const MAX_SIZE_FILE = 100;

const errorMessage = `Ошибка загрузки файла, превышен максимально допустимый размер файла (${MAX_SIZE_FILE} Мб)`;

const checkSizeFile = (size, max = MAX_SIZE_FILE) =>
  size > 0 ? size / 1048576 > max : false;

export const UploadFile = observer(
  ({
    className,
    onChange,
    label,
    accept,
    isDisabled,
    isRequired,
    error,
    multiple = true,
    files,
  }: IUploader) => {
    const [fileList, setFileList] = useState<IUploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    useStatusMessage(statusFile);
    const acceptFile = (accept) => {
      if (accept === "image") {
        return "image/*";
      }
      if (accept === "docs") {
        return "doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf";
      }
    };

    useEffect(() => {
      //console.log(fileList, "файл лист");
      onChange(fileList?.filter((f) => f.deleted === false));
    }, [JSON.stringify(fileList)]);

    useEffect(() => {
      if (Array.isArray(files)) {
        setFileList(files);
      }
    }, [JSON.stringify(files)]);

    const uploadProps = {
      action: "File/Upload",
      listType: "picture-card",
      accept: acceptFile(accept),
      multiple: multiple,
      headers: {
        Authorization: "$prefix $token",
      },
      customRequest({
        action,
        file,
        filename,
        headers,
        onError,
        onProgress,
        onSuccess,
        withCredentials,
      }) {
        const formData = new FormData();

        setUploading(true);

        if (checkSizeFile(file?.size)) {
          statusFile.setDetails([errorMessage]);
          statusFile.setStatus(Constants.requestStatuses.error);
          setUploading(false);
          onError({
            message: errorMessage,
            deleted: false,
            file,
          });
          return;
        }

        formData.append(filename, file);

        axios
          .post(action, formData, {
            withCredentials,
            headers,
            onUploadProgress: ({ total, loaded }) => {
              onProgress(
                { percent: Math.round((loaded / total) * 100).toFixed(2) },
                file,
              );
            },
          })
          .then(async (res) => {
            const file = await fileApi.getFileByGuid(res.data.guid);
            const thumbUrl = URL.createObjectURL(file.data);
            setUploading(false);
            onSuccess({
              id: res.data.id,
              guid: res.data.guid,
              deleted: false,
              thumbUrl,
            });
          })
          .catch((e) => {
            console.log(e);
            const message = checkSizeFile(file?.size)
              ? errorMessage
              : "Ошибка загрузки файла";
            statusFile.setDetails([message]);
            statusFile.setStatus(Constants.requestStatuses.error);
            setUploading(false);
            onError({
              message,
              deleted: false,
              file,
            });
          });

        return {
          abort() {
            console.log("upload progress is aborted.");
          },
        };
      },
    };

    const markFileAsDeleted: (files, file, isDeleted, callback) => void = (
      files,
      file,
      isDeleted,
      callback,
    ) => {
      const newList = file?.error
        ? files.filter((f) => f.uid !== file.uid)
        : [...files];
      const index = newList.findIndex((e) => e.uid === file.uid);
      if (index > -1) {
        newList[index] = { ...newList[index], deleted: isDeleted };
      }
      callback(newList);
    };

    const removeFile = useCallback(
      (file) => {
        markFileAsDeleted(fileList, file, true, setFileList);
      },
      [fileList],
    );

    const restoreFile = useCallback(
      (file) => {
        markFileAsDeleted(fileList, file, false, setFileList);
      },
      [fileList],
    );

    const onFilesChange = ({ file, fileList }) => {
      if (file.status === "done") {
        const index = fileList.findIndex((f) => f.uid === file.uid);
        fileList[index] = { ...fileList[index], ...file.response };
      }
      setFileList(
        multiple ? fileList : fileList.filter((f) => f.uid === file.uid),
      );
    };

    return (
      <>
        <Upload
          {...uploadProps}
          className={classnames(styles.uploader, { [className]: className })}
          disabled={isDisabled || uploading}
          fileList={fileList}
          onChange={onFilesChange}
          itemRender={(
            originNode,
            file: IUploadFile,
            currFileList: IUploadFile[],
          ) => (
            <UploadItemPreview
              file={file}
              removeFile={removeFile}
              restoreFile={restoreFile}
              fileList={currFileList}
              disabled={isDisabled || uploading}
            />
          )}
        >
          <IconButton
            className={styles.uploadButton}
            typeIcon="add"
            isDisabled={isDisabled}
            type="button"
          >
            {label}{" "}
            {isRequired ? <sup className={styles.uploaderRequired}>*</sup> : ""}
          </IconButton>
        </Upload>
        {error && <div className={styles.error}>{error}</div>}
      </>
    );
  },
);
